// scheduling/utils/csv.js
// CSV export for report tables (Trello #309).
//
// Net-new: there was no CSV or XLSX helper anywhere in client/src/scheduling/ before
// this. Kept deliberately small and dependency-free — the scheduler bundle is already
// 528 kB gzipped and a spreadsheet library would be a large addition for "download
// these rows".

// RFC 4180 quoting. A field needs quoting if it contains a comma, a quote, or any
// newline; quotes inside are doubled.
//
// This matters more than it looks: client names routinely contain commas ("Smith, Jones
// & Co"), and a schedule note can contain a newline. Without quoting those silently
// shift every following column, which is the kind of corruption nobody notices until
// they have built a decision on the wrong numbers.
function escapeField(value) {
  if (value == null) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Rows + columns -> CSV text.
 *
 * @param {Array<object>} rows
 * @param {Array<{key: string, label: string, get?: (row) => any}>} columns
 */
export function toCsv(rows, columns) {
  const head = columns.map(c => escapeField(c.label)).join(',');
  const body = rows.map(r =>
    columns.map(c => escapeField(c.get ? c.get(r) : r[c.key])).join(',')
  );
  // CRLF: Excel is the destination for essentially every export from this app, and it
  // is the line ending the spec calls for.
  return [head, ...body].join('\r\n');
}

/**
 * Trigger a browser download of `text` as `filename`.
 *
 * Prefixed with a UTF-8 BOM. Without it Excel on Windows decodes the file as the
 * system codepage, so any non-ASCII client name arrives mojibake'd — and BMSS client
 * names do contain accented characters.
 */
export function downloadCsv(filename, text) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking synchronously can cancel the download in some browsers; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `prefix-2026-09-01.csv` — dated so successive exports don't overwrite each other. */
export function datedFilename(prefix, ext = 'csv') {
  const d = new Date();
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `${prefix}-${iso}.${ext}`;
}

/** Convenience: build and download in one call. */
export function exportCsv(prefix, rows, columns) {
  downloadCsv(datedFilename(prefix), toCsv(rows, columns));
}
